import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, first, of, switchMap } from 'rxjs';
import { ChargingUser } from '../charging-user.model';
import { ChargingUserListStateService } from './charging-user-list.state.service';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { ChargingUserProcessModalComponent } from '../charging-user-process-modal/charging-user-process-modal.component';
import { ChargingUserAddIdtaginfoModalComponent } from '../charging-user-add-id-tag-info-modal/charging-user-add-id-tag-info-modal.component';
import { CharingUserInvitationsModalComponent } from '../charging-user-invitations-modal/charging-user-invitations-modal.component';
import { AppInvitationListComponent } from '../app-invitation-list-dialog/app-invitation-list-dialog.component';
import { ChargingUserAppInvitationsModalComponent } from '../app-invitation-list/app-invitations-modal.component';
import { CreateChargingUserWithAppInvitationModalComponent } from '../charging-user-create-modal/modals/create-charging-user-with-app-invitation.modal/create-charging-user-with-app-invitation.modal.component';
import { CreateChargingUserIdTagWithInvitationModalComponent } from '../charging-user-create-modal/modals/create-charging-user-id-tag-with-invitation.modal/create-charging-user-id-tag-with-invitation.modal.component';
import { CreateChargingUserIdTagWithoutInvitationModalComponent } from '../charging-user-create-modal/modals/create-charging-user-id-tag-without-invitation.modal/create-charging-user-id-tag-without-invitation.modal.component';
import { ChargingUserIdTagsModalComponent } from '../charging-user-idtags-modal/charging-user-idtags-modal.component';
import { ChargingUserAccessGroupsModalComponent } from '../charging-user-accessgroups-modal/charging-user-accessgroups-modal.component';
import { ChargingUserPreviousAccessGroupsModalComponent } from '../charging-user-previous-accessgroups-modal/charging-user-previous-accessgroups-modal.component';
import { ChargingUserEditModalComponent, ChargingUserEditModalData } from '../charging-user-edit-modal/charging-user-edit-modal.component';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import {
  ISwitchOrganizationModalData,
  SwitchOrganizationModalComponent,
} from '../switch-organization-modal/switch-organization-modal.component';
import { AppInviteIdTagUserComponent } from '../app-invite-IdTagUser/app-invite-IdTagUser.component';

@Injectable()
export class ChargingUsersOverviewDialogService {
  private dialog = inject(MatDialog);
  private state = inject(ChargingUserListStateService);
  private chargingUserService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);

  public openCreateChargingUserWithAppInvitationModal() {
    const createChargingUserModal = this.dialog.open(CreateChargingUserWithAppInvitationModalComponent, {
      data: {
        paymentTerminal: null,
      },
    });
    createChargingUserModal.afterClosed().subscribe((user: ChargingUser) => {
      if (user) {
        const sendAppInvitationModal = this.dialog.open(ChargingUserAppInvitationsModalComponent, {
          data: {
            chargingUser: user,
          },
          width: '1280px',
          height: '768px',
        });
        sendAppInvitationModal.afterClosed().subscribe(() => {
          this.state.refreshList();
        });
      }
    });
  }

  public openCreateIdTagWithInvitationModal() {
    const dialogRef = this.dialog.open(CreateChargingUserIdTagWithInvitationModalComponent, {
      data: {
        paymentTerminal: null,
      },
    });

    dialogRef.afterClosed().subscribe((user: ChargingUser) => {
      if (user) {
        this.processChargingUser(user);
      }
    });
  }

  public openCreateIdTagWithoutInvitationModal() {
    const dialogRef = this.dialog.open(CreateChargingUserIdTagWithoutInvitationModalComponent, {
      data: {
        paymentTerminal: null,
      },
    });

    dialogRef.afterClosed().subscribe((user: ChargingUser) => {
      if (user) {
        this.processChargingUser(user);
      }
    });
  }

  public viewInvitesDialog() {
    this.dialog.open(CharingUserInvitationsModalComponent, {
      width: '1024px',
    });
  }

  public viewAppInvitationsDialog() {
    const dialogRef = this.dialog.open(AppInvitationListComponent, {
      width: '1280px',
    });
    dialogRef
      .afterClosed()
      .pipe(
        filter(chargingUserId => !!chargingUserId),
        switchMap(chargingUserId => this.chargingUserService.getChargingUser(chargingUserId)),
        switchMap(chargingUser =>
          this.dialog
            .open(ChargingUserAppInvitationsModalComponent, {
              width: '1280px',
              data: { chargingUser },
            })
            .afterClosed()
        )
      )
      .subscribe(() => {
        this.state.refreshList();
      });
  }

  public processChargingUser(chargingUser: ChargingUser) {
    const dialogRef = this.dialog.open(ChargingUserProcessModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      width: '1024px',
    });

    dialogRef.afterClosed().subscribe(closeResult => {
      if (closeResult?.openAddIdTagInfoModal) {
        this.openAddIdTagInfoModal(chargingUser);
      } else {
        this.state.refreshList();
      }
    });
  }

  public openAddIdTagInfoModal(chargingUser: ChargingUser) {
    const addIdTagInfoModalRef = this.dialog.open(ChargingUserAddIdtaginfoModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      disableClose: true,
    });

    addIdTagInfoModalRef
      .afterClosed()
      .pipe(
        switchMap(() => this.chargingUserService.getChargingUser(chargingUser.id)),
        first()
      )
      .subscribe({
        next: updatedChargingUser => {
          this.processChargingUser(updatedChargingUser);
        },
        error: () => {
          this.snackbarService.error('Der skete en fejl under opdatering af brikinformation');
          this.processChargingUser(chargingUser);
        },
      });
  }

  public idTags(chargingUser: ChargingUser) {
    const dialogRef = this.dialog.open(ChargingUserIdTagsModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      width: '850px',
    });
    dialogRef.afterClosed().subscribe(closeResult => {
      if (!closeResult?.chargingUserUpdated) {
        return;
      }
      this.state.refreshChargingUser(closeResult.chargingUserUpdated);
    });
  }

  public accessGroups(chargingUser: ChargingUser) {
    const dialogRef = this.dialog.open(ChargingUserAccessGroupsModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      width: '850px',
    });
    dialogRef.afterClosed().subscribe(closeResult => {
      if (!closeResult?.chargingUserUpdated) {
        return;
      }
      this.state.refreshChargingUser(closeResult.chargingUserUpdated);
    });
  }

  public previousAccessGroups(chargingUser: ChargingUser) {
    this.dialog.open(ChargingUserPreviousAccessGroupsModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      width: '850px',
    });
  }

  public editChargingUser(chargingUser: ChargingUser) {
    const dialogRef = this.dialog.open(ChargingUserEditModalComponent, {
      data: { chargingUserId: chargingUser.id } as ChargingUserEditModalData,
      width: '612px',
    });
    dialogRef.afterClosed().subscribe(closeResult => {
      if (!closeResult?.chargingUserUpdated) {
        return;
      }
      this.state.refreshChargingUser(closeResult.chargingUserUpdated);
    });
  }

  public sendAppInvitation(chargingUser: ChargingUser) {
    const dialogRef = this.dialog.open(ChargingUserAppInvitationsModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      width: '1280px',
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(chargingUserDialogResponse => !!chargingUserDialogResponse),
        switchMap(() => this.chargingUserService.getChargingUser(chargingUser.id))
      )
      .subscribe(chargingUserResult => {
        if (chargingUserResult) {
          this.state.refreshChargingUser(chargingUserResult);
        }
      });
  }

  public openAppInviteIdTagUser(chargingUser: ChargingUser) {
    const modalRef = this.dialog.open(AppInviteIdTagUserComponent, {
      data: {
        user: chargingUser,
      },
      disableClose: true,
    });

    modalRef.afterClosed().subscribe(closeResult => {
      if (closeResult?.chargingUser) {
        this.sendAppInvitation(chargingUser);
      }
    });
  }

  public switchOrganization(chargingUser: ChargingUser) {
    const modalRef = this.dialog.open(SwitchOrganizationModalComponent, {
      data: <ISwitchOrganizationModalData>{
        departmentId: chargingUser.departmentId,
        organizationId: chargingUser.organizationId,
        chargingUserId: chargingUser.id,
      },
      disableClose: true,
    });

    modalRef
      .afterClosed()
      .pipe(
        switchMap(closeResult => {
          if (closeResult) {
            return this.chargingUserService.getChargingUser(closeResult);
          } else {
            return of(null);
          }
        })
      )
      .subscribe(newChargingUser => {
        if (newChargingUser != null) {
          this.state.refreshList();
        }
      });
  }

  public deleteChargingUser(chargingUser: ChargingUser) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Slet bruger`,
        text: `Ved slettelse af brugeren får alle brikker fjernet adgang og brugeren bliver ikke længere mulig at se for kunden. Er du sikker på du vil slette brugeren?`,
        confirmText: `Ja`,
        denyText: `Nej`,
        icon: { name: 'trash-s' },
      },
      width: '612px',
      disableClose: true,
    });

    confirmDialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.chargingUserService.deleteChargingUser(chargingUser.identifier).subscribe({
          next: () => {
            this.snackbarService.success('Bruger blev succesfuldt slettet.');
            this.state.refreshList();
          },
          error: err => {
            this.snackbarService.error(`Der skete fejl under sletning (${err.error.code})`);
          },
        });
      }
    });
  }
}
